/**
 * ReferralWidget - Compact referral widget for the parent dashboard.
 * Displays referral code, quick stats, and prompts based on user state.
 *
 * States:
 * - Has referral code: Shows code with copy/share buttons and stats
 * - Eligible without code: Prompts to get referral code
 * - Not eligible (free tier): Shows upgrade prompt
 * - First-time prompt: More prominent call-to-action (dismissable)
 */

import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "../Card";
import { Button } from "../Button";
import type { ReferralCode, ReferralStats } from "../../hooks/useReferrals";

export interface ReferralWidgetProps {
  /** User's referral code (null if not generated yet) */
  code: ReferralCode | null;
  /** Referral statistics */
  stats: ReferralStats | null;
  /** Whether the user is eligible for referrals (paid tier) */
  isEligible: boolean;
  /** Whether code generation is in progress */
  isGenerating?: boolean;
  /** Called when user wants to generate a referral code */
  onGenerateCode?: () => Promise<ReferralCode>;
  /** Called when user wants to share their code */
  onShare?: () => void;
  /** Whether this is the first time showing (for first-time prompt) */
  isFirstTimeEligible?: boolean;
}

const FIRST_TIME_DISMISSED_KEY = "jumpbean_referral_first_time_dismissed";

export function ReferralWidget({
  code,
  stats,
  isEligible,
  isGenerating = false,
  onGenerateCode,
  onShare,
  isFirstTimeEligible = false,
}: ReferralWidgetProps) {
  const [copied, setCopied] = useState(false);
  const [showFirstTimePrompt, setShowFirstTimePrompt] = useState(false);

  // Check if first-time prompt should be shown
  useEffect(() => {
    if (isFirstTimeEligible && isEligible && !code) {
      const dismissed = localStorage.getItem(FIRST_TIME_DISMISSED_KEY);
      if (!dismissed) {
        setShowFirstTimePrompt(true);
      }
    }
  }, [isFirstTimeEligible, isEligible, code]);

  const handleCopy = useCallback(async () => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [code]);

  const handleDismissFirstTime = useCallback(() => {
    localStorage.setItem(FIRST_TIME_DISMISSED_KEY, "true");
    setShowFirstTimePrompt(false);
  }, []);

  const handleGenerateCode = useCallback(async () => {
    if (onGenerateCode) {
      await onGenerateCode();
      // Dismiss first-time prompt after generating
      if (showFirstTimePrompt) {
        handleDismissFirstTime();
      }
    }
  }, [onGenerateCode, showFirstTimePrompt, handleDismissFirstTime]);

  // Not eligible (free tier) - show upgrade prompt
  if (!isEligible) {
    return (
      <Card variant="default" className="p-5 border-dashed border-2 border-neutral-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-sunshine-100 to-sunshine-200 flex items-center justify-center">
            <GiftIcon className="w-6 h-6 text-sunshine-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-body-lg text-neutral-800 mb-1">
              Refer Friends
            </h3>
            <p className="font-body text-body-sm text-neutral-600 mb-3">
              Upgrade to Family to unlock referrals! Share with friends and earn free months.
            </p>
            <Link
              to="/pricing"
              className="
                inline-flex items-center gap-1.5
                font-display font-semibold text-body-sm
                text-magic-600 hover:text-magic-700
                transition-colors
              "
            >
              Upgrade to Family
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  // First-time eligible prompt (more prominent)
  if (showFirstTimePrompt && !code) {
    return (
      <Card
        variant="elevated"
        className="relative overflow-hidden bg-gradient-to-br from-sunshine-50 via-fairy-50 to-magic-50 border-2 border-sunshine-300"
      >
        {/* Decorative sparkles */}
        <div className="absolute top-3 right-3 w-2 h-2 bg-sunshine-400 rounded-full animate-pulse" />
        <div
          className="absolute top-6 right-8 w-1.5 h-1.5 bg-fairy-400 rounded-full animate-pulse"
          style={{ animationDelay: "0.3s" }}
        />
        <div
          className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-magic-400 rounded-full animate-pulse"
          style={{ animationDelay: "0.6s" }}
        />

        <div className="relative p-5">
          {/* Header with badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sunshine-400 rounded-full">
              <SparkleIcon className="w-3.5 h-3.5 text-neutral-900" />
              <span className="font-display font-bold text-body-xs text-neutral-900 uppercase tracking-wide">
                New
              </span>
            </span>
            <h3 className="font-display font-bold text-body-lg text-neutral-800">
              Earn Free Months!
            </h3>
          </div>

          <p className="font-body text-body-md text-neutral-600 mb-4">
            Know other parents who'd love JumpBean? Share your referral code and you'll both
            get a free month when they subscribe!
          </p>

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="md"
              onClick={handleGenerateCode}
              isLoading={isGenerating}
            >
              Get Your Referral Code
            </Button>
            <button
              onClick={handleDismissFirstTime}
              className="font-body text-body-sm text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </Card>
    );
  }

  // Eligible but no code yet - show generate prompt
  if (!code) {
    return (
      <Card variant="default" className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-sunshine-100 to-sunshine-200 flex items-center justify-center">
            <GiftIcon className="w-6 h-6 text-sunshine-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-body-lg text-neutral-800 mb-1">
              Refer Friends
            </h3>
            <p className="font-body text-body-sm text-neutral-600 mb-3">
              Share JumpBean with friends! You'll both get a free month when they subscribe.
            </p>
            <Button
              variant="secondary"
              size="md"
              onClick={handleGenerateCode}
              isLoading={isGenerating}
            >
              Get Your Referral Code
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Has code - show code with stats
  const totalEarned = stats?.totalRewardValueCents
    ? `$${(stats.totalRewardValueCents / 100).toFixed(0)}`
    : "$0";
  const friendsJoined = stats?.completedReferrals ?? 0;

  return (
    <Card variant="elevated" className="p-5">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sunshine-100 to-sunshine-200 flex items-center justify-center">
          <GiftIcon className="w-5 h-5 text-sunshine-600" />
        </div>
        <h3 className="font-display font-bold text-body-lg text-neutral-800">
          Refer Friends
        </h3>
      </div>

      {/* Code display with actions */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 bg-neutral-50 border-2 border-dashed border-neutral-200 rounded-xl px-4 py-2.5">
          <p className="text-body-xs text-neutral-500 mb-0.5">Your code:</p>
          <p className="font-mono font-bold text-body-lg text-neutral-900 tracking-wider">
            {code.code}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={handleCopy}
            className={`
              p-2.5 rounded-xl border-2 transition-all duration-200 active:scale-95
              ${
                copied
                  ? "bg-meadow-100 border-meadow-400 text-meadow-600"
                  : "bg-cloud border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50"
              }
            `}
            title={copied ? "Copied!" : "Copy code"}
          >
            {copied ? (
              <CheckIcon className="w-5 h-5" />
            ) : (
              <CopyIcon className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={onShare}
            className="
              p-2.5 rounded-xl border-2
              bg-sunshine-100 border-sunshine-300 text-sunshine-700
              hover:bg-sunshine-200 hover:border-sunshine-400
              transition-all duration-200 active:scale-95
            "
            title="Share"
          >
            <ShareIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <p className="font-body text-body-sm text-neutral-500 mb-3">
        {friendsJoined} {friendsJoined === 1 ? "friend" : "friends"} joined
        {friendsJoined > 0 && <> &bull; {totalEarned} earned</>}
      </p>

      {/* Link to full page */}
      <Link
        to="/parent/referrals"
        className="
          inline-flex items-center gap-1.5
          font-display font-semibold text-body-sm
          text-magic-600 hover:text-magic-700
          transition-colors
        "
      >
        View referral details
        <ArrowRightIcon className="w-4 h-4" />
      </Link>
    </Card>
  );
}

// Icons
function GiftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
      />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

export default ReferralWidget;
